import { useState, useEffect, useCallback } from 'react';
import { buddyService, DiveBuddy, BuddyPendingResponse, UserPublicProfile } from '../services/buddyService';

export function useBuddies(userId: string | null) {
  const [buddies, setBuddies] = useState<DiveBuddy[]>([]);
  const [pending, setPending] = useState<BuddyPendingResponse>({ incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const [b, p] = await Promise.all([
        buddyService.getBuddies(userId),
        buddyService.getPending(userId),
      ]);
      setBuddies(b);
      setPending(p);
    } catch {
      setError('Failed to load buddies');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const sendRequest = useCallback(async (toUserId: string) => {
    if (!userId) return;
    await buddyService.sendRequest(userId, toUserId);
    await refresh();
  }, [userId, refresh]);

  const respond = useCallback(async (buddyUserId: string, accept: boolean) => {
    if (!userId) return;
    await buddyService.respondToRequest(userId, buddyUserId, accept);
    await refresh();
  }, [userId, refresh]);

  const remove = useCallback(async (buddyUserId: string) => {
    if (!userId) return;
    await buddyService.removeBuddy(userId, buddyUserId);
    await refresh();
  }, [userId, refresh]);

  const [searchResults, setSearchResults] = useState<UserPublicProfile[]>([]);
  const [searching, setSearching] = useState(false);

  const search = useCallback(async (query: string) => {
    if (query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const results = await buddyService.searchUsers(query);
      setSearchResults(results);
    } finally {
      setSearching(false);
    }
  }, []);

  const clearSearch = useCallback(() => setSearchResults([]), []);

  return { buddies, pending, loading, error, refresh, sendRequest, respond, remove, search, searching, searchResults, clearSearch };
}
